import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import { Box, Button, LinearProgress, Snackbar } from '@mui/material';
import React, { useState } from 'react';

import EmailCampaign from './EmailCampaign';

export type OutreachPhase = 'idle' | 'loading' | 'ready';
export interface OutreachEmailContent {
  body: string;
  subject: string;
}

interface SubcontractorOutreachProps {
  jobId?: string;
  jobInfo?: any;
  phase: OutreachPhase;
  emailContent: OutreachEmailContent | null;
  onPhaseChange: (phase: OutreachPhase) => void;
  onEmailContentChange: (content: OutreachEmailContent) => void;
}

export const generateEmailContent = (jobInfo: any): OutreachEmailContent => {
  const title = String(jobInfo?.jobTitle ?? 'the role');
  const location = String(jobInfo?.location ?? 'Location not specified');
  const duration = String(jobInfo?.duration ?? '');
  const jobId = String(jobInfo?.jobId ?? jobInfo?.id ?? '');
  const rawSkills = jobInfo?.primarySkills;
  const skills = Array.isArray(rawSkills)
    ? (rawSkills as string[]).slice(0, 4).join(', ')
    : String(rawSkills ?? 'the required skills');

  // Strip HTML tags from jobDescription for plain-text email
  const rawDesc = String(jobInfo?.jobDescription ?? '');
  const plainDesc = rawDesc
    .replace(/<[^>]*>/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();

  const subject = [
    'Opportunity:',
    title,
    location ? `– ${location},` : '',
    jobId ? `Job ID: (${jobId})` : null,
    duration ? 'Contract' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const body = [
    'Disclaimer: Please do not modify the email subject or the content of this email while replying,',
    '',
    'Hello,',
    '',
    'Hope you are doing well.',
    '',
    'We are currently looking for qualified candidates for the following opportunity with our client and would appreciate your support in sharing suitable profiles.',
    '',
    `Role: ${title}`,
    `Location: ${location}`,
    duration ? `Duration: ${duration}` : '',
    plainDesc ? '' : null,
    `Job ID: (${jobId})`,
    plainDesc ? '[BOLD]Job Description:' : null,
    plainDesc ? `[BOLD]${plainDesc}` : null,
    '',
    `We are seeking candidates with strong experience in ${skills}. The ideal candidate should be able to deliver high-quality results aligned with our client's requirements.`,
    '',
    'If you have suitable candidates available, please share their updated resumes along with contact details, availability, and rate expectations for our review.',
    '',
    'Kindly ensure the candidates meet the basic requirements before submission.',
    '',
    'Looking forward to your submissions.',
    '',
    'Best Regards,',
    'Recruiting Team',
  ]
    .filter((line) => line !== null)
    .join('\n');

  return { body, subject };
};

const SubcontractorOutreach: React.FC<SubcontractorOutreachProps> = ({
  jobId,
  jobInfo,
  phase,
  emailContent,
  onPhaseChange,
  onEmailContentChange,
}) => {
  const [snackbarMsg, setSnackbarMsg] = useState('');
  const [activeSubTab, setActiveSubTab] = useState<
    'Email Template' | 'Candidate'
  >('Email Template');

  const handleCreateEmailContent = () => {
    onPhaseChange('loading');
    setTimeout(() => {
      const content = generateEmailContent(jobInfo);
      onEmailContentChange(content);
      onPhaseChange('ready');
    }, 3000);
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      const cleanText = text.replace(/\[BOLD\]/g, '');
      await navigator.clipboard.writeText(cleanText);
      setSnackbarMsg(`${label} copied!`);
    } catch {
      setSnackbarMsg('Copy failed');
    }
  };

  const renderEmailBodyContent = () => {
    /* ── LOADING ── */
    if (phase === 'loading') {
      return (
        <Box className="subcontractor-outreach-empty">
          <Box className="subcontractor-outreach-icon-wrap">
            <ForwardToInboxOutlinedIcon className="subcontractor-outreach-icon" />
          </Box>
          <h3 className="subcontractor-outreach-title">
            Preparing Email Content
          </h3>
          <p className="subcontractor-outreach-desc">
            Generating a draft message for subcontractor outreach based on the
            job details. This may take a moment.
          </p>
          <Box className="subcontractor-outreach-loader">
            <LinearProgress className="subcontractor-loader-bar" />
          </Box>
        </Box>
      );
    }

    /* ── CONTENT ── */
    if (phase === 'ready' && emailContent) {
      return (
        <>
          <Box className="subcontractor-content">
            {/* Subject */}
            <Box className="subcontractor-section">
              <Box className="subcontractor-section-header">
                <span className="subcontractor-section-label">Subject</span>
                <Button
                  className="subcontractor-copy-btn"
                  startIcon={<ContentCopyIcon />}
                  onClick={() =>
                    void copyToClipboard(emailContent.subject, 'Subject')
                  }>
                  Copy
                </Button>
              </Box>
              <Box
                className="subcontractor-field subcontractor-subject-field"
                title={emailContent.subject}>
                {emailContent.subject}
              </Box>
            </Box>

            {/* Email Body */}
            <Box className="subcontractor-section">
              <Box className="subcontractor-section-header">
                <span className="subcontractor-section-label">Email Body</span>
                <Button
                  className="subcontractor-copy-btn"
                  startIcon={<ContentCopyIcon />}
                  onClick={() =>
                    void copyToClipboard(emailContent.body, 'Email body')
                  }>
                  Copy
                </Button>
              </Box>
              <Box className="subcontractor-field subcontractor-body-field">
                {emailContent.body.split('\n').map((line, i) => {
                  const isBold = line.startsWith('[BOLD]');
                  const displayLine = isBold
                    ? line.replace('[BOLD]', '')
                    : line;
                  return displayLine === '' ? (
                    <br key={i} />
                  ) : (
                    <p
                      key={i}
                      className="subcontractor-body-line"
                      style={{ fontWeight: isBold ? 800 : 400 }}>
                      {displayLine}
                    </p>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Snackbar
            open={!!snackbarMsg}
            autoHideDuration={2000}
            onClose={() => setSnackbarMsg('')}
            message={snackbarMsg}
            anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
          />
        </>
      );
    }

    /* ── IDLE ── */
    return (
      <Box className="subcontractor-outreach-empty">
        <Box className="subcontractor-outreach-icon-wrap">
          <ForwardToInboxOutlinedIcon className="subcontractor-outreach-icon" />
        </Box>
        <h3 className="subcontractor-outreach-title">
          Prepare Subcontractor Outreach
        </h3>
        <p className="subcontractor-outreach-desc">
          Create the subject and email message to reach out to subcontractors
          for this job. Once ready, you can copy the content and use it in your
          outreach workflow.
        </p>
        <Button
          className="subcontractor-outreach-btn"
          variant="outlined"
          startIcon={<ForwardToInboxOutlinedIcon />}
          onClick={handleCreateEmailContent}>
          Create Email Content
        </Button>
      </Box>
    );
  };

  return (
    <Box className="subcontractor-outreach-container">
      <Box
        className="matches-tabs subcontractor-sub-tabs"
        sx={{
          borderBottom: '1px solid #E2E8F0',
          marginBottom: '16px',
          paddingBottom: '0',
        }}>
        <Box className="tabs-list">
          <Box
            className={`tab ${
              activeSubTab === 'Email Template' ? 'active' : ''
            }`}
            onClick={() => setActiveSubTab('Email Template')}>
            Email Template
          </Box>
          <Box
            className={`tab ${activeSubTab === 'Candidate' ? 'active' : ''}`}
            onClick={() => setActiveSubTab('Candidate')}>
            Candidate
          </Box>
        </Box>
      </Box>
      <Box className="subcontractor-sub-content">
        {activeSubTab === 'Email Template' && renderEmailBodyContent()}
        {activeSubTab === 'Candidate' && (
          <EmailCampaign jobId={jobId} jobInfo={jobInfo} />
        )}
      </Box>
    </Box>
  );
};

export default SubcontractorOutreach;
